import { removeComma, thousandSeperatorWithPrefix } from 'utils'

/**
 * Sorting helpers shared by the Trust Ledger grids. Mirrors the backend rules in
 * `modules/loan/servicing/trust-ledger-all.utils.ts` - keep the two in step, or the same
 * ledger reads in a different order depending on which tab you look at it from.
 */

/**
 * One comparable key for a ledger date.
 *
 * These rows carry whatever form wrote them: `YYYY-MM-DD` from the date pickers, `MM/DD/YYYY`
 * on imported rows, sometimes a full timestamp. Compared as raw strings every slash date sorts
 * ahead of every ISO one; compared through `new Date()` a bare ISO date parses as UTC while a
 * slash date parses as local, so two rows on the same day come out in opposite orders. A plain
 * calendar key sidesteps both. An unreadable date sorts first rather than throwing the order off.
 */
export const ledgerDateKey = (date?: string | null): string => {
  const value = String(date ?? '').trim()
  if (!value) return ''

  const iso = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/)
  if (iso) return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`

  const slash = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (slash) return `${slash[3]}-${slash[1].padStart(2, '0')}-${slash[2].padStart(2, '0')}`

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  const month = String(parsed.getMonth() + 1).padStart(2, '0')
  const day = String(parsed.getDate()).padStart(2, '0')
  return `${parsed.getFullYear()}-${month}-${day}`
}

/** Ledger rows oldest first, which is the order every running balance is walked in. */
export const byLedgerDateAsc = <T extends Record<string, any>>(rows: T[]): T[] =>
  [...rows].sort((a, b) => {
    const dateA = ledgerDateKey(a?.date)
    const dateB = ledgerDateKey(b?.date)
    if (dateA !== dateB) return dateA < dateB ? -1 : 1
    // Rows sharing a day keep the order they were stored in, so the walk is deterministic.
    return 0
  })

/**
 * The grids open newest first. The Construction Reserve grid stacks Date and Files under one
 * heading, so its date column is keyed differently from the sub-ledgers'.
 */
export const LEDGER_DEFAULT_SORT_KEY = 'date'
export const CONSTRUCTION_LEDGER_DEFAULT_SORT_KEY = 'dateWithFiles'
export const LEDGER_DEFAULT_SORT_ORDER = -1 as const

/**
 * Rows the Construction Reserve grid opens on. The reader can change it from the grid's own
 * "Per page" select; this is only where it starts, and it matches the 10 every other paged grid
 * in the app opens on.
 */
export const CONSTRUCTION_LEDGER_ROWS_PER_PAGE = 10

/** Money on the ledger grids always shows cents and rounds rather than truncating. */
export const ledgerMoney = (value: any): string => thousandSeperatorWithPrefix(removeComma(value))
